import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FileText, AlertTriangle, Shield, Users } from 'lucide-react';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const SupervisorOverview = ({ user }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    activeJsas: 0,
    pendingIncidents: 0,
    completedAudits: 0,
    teamMembers: 0
  });

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    setLoading(true);
    try {
      const [jsasRes, incidentsRes, auditsRes, teamRes] = await Promise.all([
        axiosInstance.get('/jsa').catch(() => ({ data: [] })),
        axiosInstance.get('/incident-reports').catch(() => ({ data: [] })),
        axiosInstance.get('/safety-audits').catch(() => ({ data: [] })),
        axiosInstance.get('/admin/users').catch(() => ({ data: [] }))
      ]);

      setStats({
        activeJsas: jsasRes.data.length || 0,
        pendingIncidents: incidentsRes.data.filter(i => i.status !== 'resolved').length || 0,
        completedAudits: auditsRes.data.length || 0,
        teamMembers: teamRes.data.filter(u => u.role === 'worker').length || 0
      });
    } catch (error) {
      console.error('Failed to load stats:', error);
      toast.error(t('supervisorPages.failedToLoadStats'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <Button
          variant="ghost"
          onClick={() => window.history.back()}
          className="mb-4 hover:bg-gray-100"
          data-testid="back-button"
        >
          {t('common.back')}
        </Button>

        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">{t('supervisorPages.overview')}</h1>
          <p className="text-gray-600 mt-1">{t('supervisorPages.overviewDesc')}</p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-blue-600" />
                  {t('supervisorPages.activeJsas')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-gray-900">{stats.activeJsas}</div>
                <p className="text-xs text-gray-500 mt-1">{t('supervisorPages.jobSafetyAnalyses')}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-orange-600" />
                  {t('supervisorPages.pendingIncidents')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-orange-600">{stats.pendingIncidents}</div>
                <p className="text-xs text-gray-500 mt-1">{t('supervisorPages.requiresAttention')}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                  <Shield className="w-4 h-4 text-green-600" />
                  {t('supervisorPages.completedAudits')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-green-600">{stats.completedAudits}</div>
                <p className="text-xs text-gray-500 mt-1">{t('supervisorPages.safetyAudits')}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                  <Users className="w-4 h-4 text-purple-600" />
                  {t('supervisorPages.teamMembers')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-purple-600">{stats.teamMembers}</div>
                <p className="text-xs text-gray-500 mt-1">{t('supervisorPages.workers')}</p>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default SupervisorOverview;
